// The Provider storefront dataset ("store" page mode) -- the same shape the Seller shop templates
// and shop elements bind to, filled with PROVIDER data only (never Seller products):
//   storeType            "provider": PageRenderer's Provider store configuration keys off it
//   products             the Provider's active products (full catalogue items + the display fields
//                        the shop templates bind to: ProductImageUrls, price...). They never behave as
//                        retail stock: the Provider store configuration turns every product action into
//                        the professional product page (Buy Wholesale / Add to My Shop).
//   providerProducts     same list (the name the Provider builder documents), plus
//   wholesaleProducts / dropshippingProducts / newProducts
//   provider             identity + REAL statistics (absent figure = hidden, never a placeholder)
//   categories           the Provider's real categories (same shape as the Seller shop matrix)
// No private field (address lines, phone, email, inventory detail) is ever exposed.
import { toImageUrl } from "../../../config";
import { providerStorefrontPath, providerCatalogPath } from "../providerWorkspace";
import { providerShopDisplayName, providerShopMonogram } from "../utils/providerShopIdentity";
import { elakayAutoText } from "../../pageInside/dataElakay";

// Public shop name: the Provider's shop name, else the owner's initials -- never the full name.
const nameOf = (profile) => providerShopDisplayName(profile?.shop, profile?.provider) || elakayAutoText("h_dsw_role_provider", "Provider");
const time = (d) => (d ? new Date(d).getTime() || 0 : 0);

export const mapProviderProduct = (p) => {
  const images = (p.images || []).map((src) => toImageUrl(src));
  const categoryIds = [...new Set([...(p.categoryIds || []), p.categoryId].filter(Boolean).map(String))];
  return {
    ...p,
    image: images[0] || "",
    ProductImageUrls: images,
    price: p.dropshippingPrice ?? p.basePrice ?? null,
    currency: p.currency || "USD",
    minimumOrderQuantity: p.minimumOrderQuantity || 1,
    wholesale: Boolean(p.wholesaleEnabled),
    dropshipping: Boolean(p.dropshippingEnabled),
    categoriesID: categoryIds.map((categoryID) => ({ categoryID })),
    url: `/seller/provider-products/${p._id}`,
  };
};

// `profile` = storefront summary ({ provider, shop, stats }); `products` = catalogue items;
// `categories` = facets categories ([{ _id, name: [...], products }]).
export const buildStorefrontPageData = (profile, products = [], categories = []) => {
  const providerId = profile?.provider?._id ? String(profile.provider._id) : "";
  const shopName = nameOf(profile);
  const list = (products || []).map(mapProviderProduct);
  const stats = profile?.stats || {};
  const total = Number.isFinite(stats.activeProducts) ? stats.activeProducts : list.length;
  return {
    storeType: "provider",
    products: list,
    providerProducts: list,
    wholesaleProducts: list.filter((p) => p.wholesale),
    dropshippingProducts: list.filter((p) => p.dropshipping),
    newProducts: [...list].sort((a, b) => time(b.createdAt) - time(a.createdAt)),
    provider: {
      _id: providerId,
      shopName,
      monogram: providerShopMonogram(profile?.shop, profile?.provider),
      roleName: profile?.provider?.roleName || "Provider",
      // What the storefront may offer (Provider Settings + real products, computed server-side);
      // absent (older response / draft preview) = decided per product only.
      capabilities: profile?.capabilities || null,
      location: profile?.shop?.location || "",
      avatar: profile?.shop?.avatar ? toImageUrl(profile.shop.avatar) : "",
      stats,
      totalProducts: total,
      storefrontUrl: providerId ? providerStorefrontPath(providerId) : "",
      catalogUrl: providerId ? providerCatalogPath(providerId) : "",
    },
    owner: { _id: providerId, shopName: [shopName], avatar: profile?.shop?.avatar || "" },
    categories: { shopName, owner: providerId, categories: (categories || []).map((c) => ({ ...c, name: Array.isArray(c.name) ? c.name : [String(c.name || "")] })) },
    pubCategoryProdMatrix: { shopName, products: [] },
    sliderShop: [],
    filters: [],
    siseProduct: total,
    siseProductAll: total,
    size: list.length,
    shopActive: true,
    links: {
      homePage: "/",
      shopPage: providerId ? providerStorefrontPath(providerId) : "/seller/providers",
      aboutPage: providerId ? providerStorefrontPath(providerId) : "/seller/providers",
      productDetail: "/seller/provider-products",
      catalog: providerId ? providerCatalogPath(providerId) : "/seller/provider-catalog",
      dropshippingIn: "/seller/dropshipping-in",
    },
  };
};

export const buildStorefrontDatasets = (profile, products, categories) => ({
  store: {
    label: elakayAutoText("h_pvc_dataset_label", "{value0} — Provider storefront", { value0: nameOf(profile) }),
    pageMode: "store",
    data: buildStorefrontPageData(profile, products, categories),
    isLive: true,
  },
});

export const storefrontShareUrl = (profile) => {
  const id = profile?.provider?._id;
  if (!id) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${providerStorefrontPath(id)}`;
};
